import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, functions } from "@/lib/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import PostingResultDialog from "@/components/modals/PostingResultDialog.jsx";
import { ToastContext } from "@/components/ToastProvider";
import { formatDT } from "@/utils/dates";

function StatusBadge({ s }) {
  const cls =
    s === "paid"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : s === "rejected"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : "bg-amber-50 text-amber-800 border-amber-200";
  const icon =
    s === "paid"
      ? (
          <svg className="inline mr-1" width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#34d399"/><path d="M6 10.5l3 3 5-6" stroke="#065f46" strokeWidth="2" fill="none"/></svg>
        )
      : s === "rejected"
      ? (
          <svg className="inline mr-1" width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#f87171"/><path d="M7 7l6 6M13 7l-6 6" stroke="#7f1d1d" strokeWidth="2" fill="none"/></svg>
        )
      : (
          <svg className="inline mr-1" width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#fbbf24"/><path d="M10 5v5m0 3h.01" stroke="#92400e" strokeWidth="2" fill="none"/></svg>
        );
  return <span className={`inline-block text-xs px-2 py-0.5 rounded border ${cls}`}>{icon}{s}</span>;
}

export default function NewMembersPayment({ id, onClose, adminUid, resultRedirectPath = "/admin/coop-membership", fallbackRedirectPath = "/admin/coop-membership", disableAutoPosting = false, onPaymentVerified = null, isCoopMembershipVerification = true, memberData = null }) {
  const { notify } = useContext(ToastContext);
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [principal, setPrincipal] = useState("");
  const [interest, setInterest] = useState("");
  const [notes, setNotes] = useState("");
  const [journal, setJournal] = useState(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultVariant, setResultVariant] = useState("success");
  const [resultInfo, setResultInfo] = useState({ receiptNo: null, journalNo: null, message: "" });
  const [cashAccounts, setCashAccounts] = useState([]);
  const [depositAccountId, setDepositAccountId] = useState("");
  const [showRefund, setShowRefund] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundInfo, setRefundInfo] = useState({ ok: false, journalId: "", already: false });
  const [showVoid, setShowVoid] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);
  
  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({
    amount: false,
    audit: false,
    info: false,
    proof: false,
    loan: false,
    status: false
  });
  
  const navigate = useNavigate();
  const postUnsubRef = useRef(null);
  const postTimerRef = useRef(null);

  const pad5 = (v) => (v != null ? String(v).padStart(5, "0") : "");

  // Toggle section collapse state
  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Collapsible section component
  const CollapsibleSection = ({ id, title, children, defaultCollapsed = false }) => {
    const isCollapsed = collapsedSections[id] ?? defaultCollapsed;
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 w-full hover:shadow-md transition-shadow">
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between text-sm font-semibold mb-3 hover:bg-gray-50 dark:hover:bg-gray-700 p-2 -m-2 rounded transition-colors text-gray-900 dark:text-white"
        >
          <span>{title}</span>
          <svg
            className={`w-4 h-4 transition-transform text-gray-500 dark:text-gray-400 ${isCollapsed ? 'rotate-90' : '-rotate-90'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <div className={`transition-all duration-200 overflow-hidden ${
          isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'}`}>
          {children}
        </div>
      </div>
    );
  };

  // Lock background scroll while modal is open
  useEffect(() => {
    // Remove the problematic body scroll lock that was causing freezing
    // The modal will handle its own scrolling properly
    return () => {
      // Cleanup function - no need to restore body overflow
    };
  }, []);

  function cleanupPostingWatch() {
    if (postUnsubRef.current) {
      try { postUnsubRef.current(); } catch {}
      postUnsubRef.current = null;
    }
    if (postTimerRef.current) {
      clearTimeout(postTimerRef.current);
      postTimerRef.current = null;
    }
  }

  useEffect(() => () => cleanupPostingWatch(), []);

  useEffect(() => {
    if (!id) return; // Guard against undefined id
    const ref = doc(db, "payments", id);
    const unsub = onSnapshot(ref, (s) => {
      const d = s.exists() ? { id: s.id, ...s.data() } : null;
      console.log('🔍 NewMembersPayment - Payment data received:', { id, payment: d });
      setP(d);
      if (d) {
        setNotes(d.notes || "");
        if (d.type === "loan_repayment") {
          setPrincipal(String(d.principalPortion ?? d.amount ?? ""));
          setInterest(String(d.interestPortion ?? 0));
        }
        setDepositAccountId(d.depositAccountId || "");
      }
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const qj = query(collection(db, "journalEntries"), where("linkedPaymentId", "==", id), limit(1));
    const unsub = onSnapshot(qj, (s) => {
      setJournal(s.empty ? null : { id: s.docs[0].id, ...s.docs[0].data() });
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setAuditLoading(true);
    const ql = query(collection(db, "adminLogs"), where("paymentId", "==", id), orderBy("ts", "desc"), limit(20));
    const unsub = onSnapshot(ql, (snap) => {
      setAuditLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setAuditLoading(false);
    }, () => {
      setAuditLogs([]);
      setAuditLoading(false);
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    const fn = async () => {
      try {
        const snap = await getDocs(collection(db, "accounts"));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const candidates = rows.filter((a) => {
          const t = String(a.type || "").toLowerCase();
          const n = `${a.main} ${a.individual || ""}`.toLowerCase();
          return t === "asset" || n.includes("cash") || n.includes("bank") || n.includes("gcash");
        });
        setCashAccounts(candidates);
      } catch {}
    };
    fn();
  }, []);

  // Don't render if no ID is provided
  if (!id) return null;
  
  const journalRefNumber = (journal && (journal.refNumber || (journal.journalNo != null ? pad5(journal.journalNo) : ""))) || "";
  const receiptNo = p?.receiptNo || "";
  if (!p) return null;
  const amount = Number(p.amount || 0);
  const isLoan = p.type === "loan_repayment";
  
  // Check if payment is already posted to accounting
  const isAlreadyPosted = p.status === "paid" && (p.receiptNo || p.journalNo);
  
  // For cooperative membership verification, check member's posted status
  let isCoopPaymentAlreadyPosted = false;
  if (isCoopMembershipVerification && memberData && p) {
    if (p.type === "membership_fee") {
      isCoopPaymentAlreadyPosted = memberData.entranceFeePosted === true;
    } else if (p.type === "share_capital") {
      isCoopPaymentAlreadyPosted = memberData.shareCapitalPosted === true;
    }
  }
  
  const canAct = (p.status === "pending" || (p.status === "paid" && !isAlreadyPosted)) && !isCoopPaymentAlreadyPosted && !busy;
  

  
  // Button should be disabled if:
  // 1. Payment is already posted to accounting (has receiptNo or journalNo)
  // 2. Payment is not in a state where it can be acted upon
  // 3. System is busy
  
  const canRefund = p.status === "paid" && !refundBusy;
  const canVoid = p.status === "pending" && !voidBusy;

  const pNum = Number(principal || 0);
  const iNum = Number(interest || 0);
  const sumOk = !isLoan || Math.abs(pNum + iNum - amount) < 0.005;

  function navigateIfNeeded(path) {
    if (path === null) return; // explicitly disabled
    if (typeof path === "string" && path.length) navigate(path);
  }

  // Automatically update member verification status when payments are posted
  const updateMemberVerificationStatus = async (memberUid, paymentType, isPosted) => {
    try {
      const updates = {};
      
      console.log('Updating member verification status:', { memberUid, paymentType, isPosted });
      
      if (paymentType === 'membership_fee') {
        updates.entranceFeePosted = isPosted;
        updates.entranceFeeVerified = isPosted;
        // Also update the payment flag for UI consistency
        updates.entranceFeePayment = isPosted;
      } else if (paymentType === 'share_capital') {
        updates.shareCapitalPosted = isPosted;
        updates.shareCapitalVerified = isPosted;
        // Also update the payment flag for UI consistency
        updates.shareCapitalPayment = isPosted;
      }

      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, 'members', memberUid), updates);
        console.log('Member verification status updated successfully:', updates);
      } else {
        console.warn('No updates to apply for payment type:', paymentType);
      }
    } catch (error) {
      console.error('Error updating member verification status:', error);
      throw error; // Re-throw to handle in calling function
    }
  };

  function watchForPosting(paymentId) {
    cleanupPostingWatch();
    const pref = doc(db, "payments", paymentId);

    // safety timeout: optimistic success and optional redirect
    postTimerRef.current = setTimeout(() => {
      setIsApproving(false);
      setResultVariant("success");
      setResultInfo({ receiptNo: null, journalNo: null, message: "" });
      setResultOpen(true);
      // Do not auto-redirect here; let dialog onClose handle it via props
    }, 15000);

    postUnsubRef.current = onSnapshot(
      pref,
      (snap) => {
        const v = snap.data();
        if (!v) return;
        if (v.postingError) {
          cleanupPostingWatch();
          setIsApproving(false);
          setResultVariant("error");
          const msg = v.postingError?.message || String(v.postingError);
          setResultInfo({ receiptNo: null, journalNo: null, message: msg });
          setResultOpen(true);
          return;
        }
        if (v.status === "paid" && (v.receiptNo || v.journalNo)) {
          cleanupPostingWatch();
          setIsApproving(false);
          setResultVariant("success");
          setResultInfo({ receiptNo: v.receiptNo || null, journalNo: v.journalNo || null, message: "" });
          setResultOpen(true);
          
          // Automatically update member verification status for cooperative membership
          if (isCoopMembershipVerification && memberData && memberData.uid) {
            updateMemberVerificationStatus(memberData.uid, p.type, true);
          }
        }
      },
      () => {
        cleanupPostingWatch();
        setIsApproving(false);
        setResultVariant("error");
        setResultInfo({ receiptNo: null, journalNo: null, message: "Listener error" });
        setResultOpen(true);
      }
    );
  }

  async function approve() {
    setErr("");
    setSuccess("");
    if (!sumOk) {
      setErr("Principal + Interest must equal Amount.");
      return;
    }
    if (["gcash_manual", "bank_transfer", "static_qr"].includes(p.method || "") && !p.proofURL) {
      setErr("Proof file is required for manual methods.");
      return;
    }
    setBusy(true);
    setIsApproving(true);
    try {
      const ref = doc(db, "payments", id);
      await updateDoc(ref, {
        confirmedBy: adminUid || null,
        confirmedAt: serverTimestamp(),
        notes: (notes || "").trim(),
        ...(depositAccountId ? { depositAccountId } : {}),
        ...(isLoan ? { principalPortion: Number(Number(pNum).toFixed(2)), interestPortion: Number(Number(iNum).toFixed(2)) } : {}),
      });
      await updateDoc(ref, { status: "paid" });
      
      // For cooperative membership verification, post to accounting AND update member status
      if (isCoopMembershipVerification && memberData && memberData.uid) {
        // Still post to accounting for cooperative membership
        if (!disableAutoPosting) {
          watchForPosting(id);
          setSuccess("Payment verified and posting to accounting...");
        } else {
          setIsApproving(false);
          setSuccess("Payment verified successfully.");
          onPaymentVerified?.(p);
        }
      } else {
        // Regular payment flow
        if (!disableAutoPosting) {
          watchForPosting(id);
          setSuccess("Posting…");
        } else {
          setIsApproving(false);
          setSuccess("Payment verified successfully.");
          onPaymentVerified?.(p);
        }
      }
      
      await addDoc(collection(db, "adminLogs"), {
        ts: serverTimestamp(),
        actorUid: adminUid || null,
        action: "PAYMENT_APPROVAL",
        paymentId: id,
        paymentType: p.type || null,
        amount: amount,
        memberUid: p.userId || p.uid || null,
        ...(isLoan ? { principalPortion: pNum, interestPortion: iNum } : {}),
      });
    } catch (e) {
      setErr(e?.message || String(e));
      setIsApproving(false);
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    const reason = window.prompt("Reason for rejection (required):");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    setErr("");
    setSuccess("");
    try {
      const ref = doc(db, "payments", id);
      await updateDoc(ref, {
        status: "rejected",
        confirmedBy: adminUid || null,
        confirmedAt: serverTimestamp(),
        notes: `${(notes || "").trim()}${notes ? " | " : ""}Rejected: ${reason.trim()}`,
      });
      
      // For cooperative membership verification, update member status
      if (isCoopMembershipVerification && memberData && memberData.uid) {
        await updateMemberVerificationStatus(memberData.uid, p.type, false);
        setSuccess("Payment rejected and member status updated.");
        
        // Close the modal after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setSuccess("Payment rejected.");
      }
      
      await addDoc(collection(db, "adminLogs"), {
        ts: serverTimestamp(),
        actorUid: adminUid || null,
        action: "PAYMENT_REJECTION",
        paymentId: id,
        paymentType: p.type || null,
        amount: amount,
        memberUid: p.userId || p.uid || null,
        reason: reason.trim(),
      });
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[99999] flex items-start justify-center p-2 sm:p-4 touch-none overflow-y-auto bg-black/20">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
              {/* Modal Container */}
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl min-h-fit max-h-[95vh] overflow-hidden my-2 sm:my-4 touch-auto border border-gray-200 dark:border-gray-700">
        {/* Header - Always visible */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 rounded-t-xl z-20 flex-shrink-0 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-lg font-semibold break-words text-gray-900 dark:text-white">New Member Payment - {p.memberName || p.userEmail || p.userId || p.uid || "Member"}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 break-words">{p.type || "—"} • {p.method === "static_qr" ? "QR (manual)" : (p.method || "—")}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StatusBadge s={p.status || "pending"} />
              <button 
                className="rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors" 
                onClick={onClose} 
                aria-label="Close"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content with Enhanced Scrollbar */}
        <div className="overflow-y-auto max-h-[calc(95vh-80px)] custom-scrollbar" style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#d1d5db #f3f4f6'
        }}>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 bg-gray-50 dark:bg-gray-900">
          {/* Amount & Receipt / Journal */}
          <CollapsibleSection id="amount" title="Amount & Receipt / Journal" defaultCollapsed={false}>
            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Amount</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-4">₱{Number(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="mt-4 text-sm space-y-3">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                <span className="flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-400">Ref#:</span>
                  <b className="break-all text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{journalRefNumber || "—"}</b>
                </span>
                {receiptNo && (
                  <span className="flex items-center gap-2">
                    <span className="text-gray-600 dark:text-gray-400">Receipt:</span>
                    <b className="text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{receiptNo}</b>
                  </span>
                )}
                {journalRefNumber && (
                  <span className="flex items-center gap-2">
                    <span className="text-gray-600 dark:text-gray-400">Journal No:</span>
                    <b className="text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{journalRefNumber}</b>
                  </span>
                )}
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded">Posting: {(p.posting?.status || "—")} • Attempts: {p.posting?.attempts || 0}</div>
            {p.postingError && (
              <div className="mt-4 rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200 p-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 text-rose-500">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium mb-2">Posting Error</div>
                    <div className="break-words text-rose-700 dark:text-rose-300">{p.postingError?.message || String(p.postingError)}</div>
                    <div className="mt-3 flex flex-col sm:flex-row gap-2 items-start">
                      <button className="btn btn-sm btn-outline border-rose-200 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30" onClick={() => navigator.clipboard?.writeText(p.postingError?.message || String(p.postingError))}>Copy Error</button>
                      <a className="btn btn-sm btn-outline border-rose-200 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30" href="/admin/settings/accounting" target="_self">Open Accounting Settings</a>
                      <button className="btn btn-sm btn-primary bg-rose-600 hover:bg-rose-700" onClick={async () => { setRetryBusy(true); try { const call = httpsCallable(functions, "repostPayment"); await call({ paymentId: p.id }); } catch (e) { notify(e?.message || String(e), { type: "error" }); } finally { setRetryBusy(false); } }} disabled={retryBusy}>
                        {retryBusy ? "Retrying…" : "Retry Post"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {p.posting?.status === "failed" && (
              <div className="mt-4 rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200 p-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 text-rose-500">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium mb-2">Posting Failed</div>
                    <div className="break-words text-rose-700 dark:text-rose-300">{p.posting?.error?.message || p.posting?.error || "Unknown error occurred"}</div>
                    <div className="mt-3 flex flex-col sm:flex-row gap-2 items-start">
                      <button className="btn btn-sm btn-outline border-rose-200 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30" onClick={() => navigator.clipboard?.writeText(p.posting?.error?.message || p.posting?.error || "")}>Copy Error</button>
                      <a className="btn btn-sm btn-outline border-rose-200 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30" href="/admin/settings/accounting" target="_self">Open Accounting Settings</a>
                      <button className="btn btn-sm btn-primary bg-rose-600 hover:bg-rose-700" onClick={async () => { setRetryBusy(true); try { const call = httpsCallable(functions, "repostPayment"); await call({ paymentId: p.id }); } catch (e) { notify(e?.message || String(e), { type: "error" }); } finally { setRetryBusy(false); } }} disabled={retryBusy}>
                        {retryBusy ? "Retrying…" : "Retry Post"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {p.status === "paid" && p.receiptNo && (
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <a className="btn btn-sm btn-primary bg-green-600 hover:bg-green-700 text-white" href={`/receipt/${p.id}`} target="_self">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View Receipt
                </a>
                <button className="btn btn-sm btn-outline border-green-200 text-green-700 hover:bg-green-50 dark:hover:bg-green-900/30" onClick={async () => { try { await navigator.clipboard?.writeText(`${location.origin}/receipt/${p.id}`); notify("Receipt link copied.", { type: "success", duration: 2500 }); } catch {} }}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy Link
                </button>
              </div>
            )}
          </CollapsibleSection>

          {/* Audit Trail */}
          <CollapsibleSection id="audit" title="Audit Trail" defaultCollapsed={true}>
            {auditLoading ? (
              <div className="text-xs text-ink/60">Loading…</div>
            ) : auditLogs.length === 0 ? (
              <div className="text-xs text-ink/60">No admin actions found.</div>
            ) : (
              <ul className="space-y-1">
                {auditLogs.map((log) => (
                  <li key={log.id} className="text-xs flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="text-ink/60 shrink-0">{log.ts?.toDate ? formatDT(log.ts.toDate()) : "—"}</span>
                    <span className="font-mono bg-gray-100 rounded px-1 shrink-0">{log.action}</span>
                    <span className="text-ink/80 break-all">{log.actorUid || "?"}</span>
                    {log.reason && <span className="text-ink/60 break-words">{log.reason}</span>}
                    {log.amount != null && <span className="text-ink/60 shrink-0">₱{Number(log.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleSection>

          {/* Info */}
          <CollapsibleSection id="info" title="Payment Information" defaultCollapsed={true}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-ink/60">Reference No</div>
                <div className="break-all">{p.referenceNo || p.refNo || "—"}</div>
              </div>
              {p.method === "cod" && (
                <div>
                  <div className="text-ink/60">Collected</div>
                  <div className="break-words">
                    {p.collectedBy ? (
                      <>
                        by <span className="font-mono">{p.collectedBy}</span>
                        {p.collectedAt && (
                          <span className="text-ink/60"> • {formatDT(p.collectedAt)}</span>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              )}
              {p.method === "gcash_manual" && (
                <>
                  <div>
                    <div className="text-ink/60">GCash Number</div>
                    <div className="font-mono break-all">{p.gcashNumber || "—"}</div>
                  </div>
                  <div>
                    <div className="text-ink/60">GCash Name</div>
                    <div className="break-words">{p.gcashName || "—"}</div>
                  </div>
                </>
              )}
              {p.method === "bank_transfer" && (
                <>
                  <div>
                    <div className="text-ink/60">Bank</div>
                    <div className="break-words">{p.bankName || "—"}</div>
                  </div>
                  <div>
                    <div className="text-ink/60">Account</div>
                    <div className="font-mono break-all">{p.bankAccount || "—"}</div>
                  </div>
                </>
              )}
              {p.method === "static_qr" && (
                <div className="sm:col-span-2">
                  <div className="text-ink/60">QR Code</div>
                  <div className="break-all">{p.qrCode || "—"}</div>
                </div>
              )}
              {p.notes && (
                <div className="sm:col-span-2">
                  <div className="text-ink/60">Notes</div>
                  <div className="break-words">{p.notes}</div>
                </div>
              )}
              {p.loanId && (
                <div className="sm:col-span-2">
                  <div className="text-ink/60">Loan</div>
                  <div className="break-all">{p.loanId}</div>
                </div>
              )}
              {p.loanId && (
                <div className="sm:col-span-2">
                  <div className="text-ink/60">Created</div>
                  <div>{formatDT(p.createdAt)}</div>
                </div>
              )}
              <div className="sm:col-span-2">
                <div className="text-ink/60">Linked</div>
                <div>
                  {p.linkedId ? (
                    p.type === "purchase" ? (
                      <a className="underline break-all" href={`/orders/${p.linkedId}`}>View Order</a>
                    ) : p.type === "loan_repayment" ? (
                      <a className="underline break-all" href={`/loans/${p.linkedId}`}>View Loan</a>
                    ) : (
                      <span className="break-all">{p.linkedId}</span>
                    )
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-ink/60">Notes</div>
                <textarea className="w-full border rounded px-2 py-1" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <div className="text-ink/60">Deposit to (optional)</div>
                <select className="input w-full" value={depositAccountId} onChange={(e) => setDepositAccountId(e.target.value)}>
                  <option value="">— Default via settings —</option>
                  {cashAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.code ? `${a.code} • ` : "") + a.main + (a.individual ? " / " + a.individual : "")}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-ink/60 mt-1">Overrides the cash account for this payment only.</div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Proof viewer */}
          <CollapsibleSection id="proof" title="Proof of Payment" defaultCollapsed={true}>
            {(() => {
              console.log('🔍 Proof section - Payment data:', { 
                paymentId: p?.id, 
                type: p?.type, 
                proofURL: p?.proofURL,
                hasProof: !!p?.proofURL,
                allFields: Object.keys(p || {})
              });
              return null;
            })()}
            {p.proofURL ? (
              <div className="space-y-2">
                {String(p.proofURL).match(/\.(png|jpe?g|webp|gif)$/i) ? (
                  <img src={p.proofURL} alt="Proof" className="max-h-[60vh] w-auto rounded border" />
                ) : String(p.proofURL).match(/\.(pdf)$/i) ? (
                  <iframe title="Proof" src={p.proofURL} className="w-full h-[60vh] border rounded" />
                ) : (
                  <a className="underline break-all" href={p.proofURL} target="_blank" rel="noreferrer">Open proof</a>
                )}
                <div>
                  <a className="btn btn-sm btn-outline" href={p.proofURL} target="_blank" rel="noreferrer">Open in new tab</a>
                </div>
              </div>
            ) : (
              <div className="text-sm text-ink/60">No proof uploaded.</div>
            )}
          </CollapsibleSection>

          {/* Loan split */}
          {isLoan && (
            <CollapsibleSection id="loan" title="Loan Split" defaultCollapsed={true}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-ink/60">Principal Portion</span>
                  <input className="input w-full" type="number" step="0.01" value={principal} onChange={(e) => setPrincipal(e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-xs text-ink/60">Interest Portion</span>
                  <input className="input w-full" type="number" step="0.01" value={interest} onChange={(e) => setInterest(e.target.value)} />
                </label>
              </div>
              <div className={`mt-2 text-sm ${sumOk ? "text-emerald-700" : "text-rose-700"}`}>
                {sumOk ? "OK" : `Must equal total amount ₱${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </div>
              <div className="text-xs text-ink/60 mt-1">Interest → credited to Interest Income.</div>
            </CollapsibleSection>
          )}

          {/* Status banners */}
          <CollapsibleSection id="status" title="Status Information" defaultCollapsed={true}>
            {(p.status === "refunded" || p.refundJournalId) && (
              <div className="rounded border border-amber-200 bg-amber-50 text-amber-800 p-2 text-sm">
                <div className="break-words">
                  Refunded{p.refundJournalId ? (
                    <> • Journal: <a className="underline break-all" href={`/admin/journals/${p.refundJournalId}`}>{p.refundJournalId}</a></>
                  ) : null}
                  {p.refundReason ? <> • Reason: {p.refundReason}</> : null}
                </div>
              </div>
            )}
            {p.status === "voided" && (
              <div className="rounded border border-gray-200 bg-gray-50 text-gray-800 p-2 text-sm">
                <div className="break-words">
                  Voided{p.voidReason ? <> • Reason: {p.voidReason}</> : null}
                </div>
              </div>
            )}
          </CollapsibleSection>
          
          {/* Actions */}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button className="btn btn-primary flex-1 sm:flex-none justify-center" disabled={!canAct || (isLoan && !sumOk) || isApproving} onClick={approve}>
              {isApproving ? "Posting…" : "Verify & Approve Payment"}
            </button>
            <button className="btn btn-outline flex-1 sm:flex-none justify-center" disabled={!canAct} onClick={reject}>Reject</button>
            {p.status === "paid" && (
              <button className="btn btn-outline btn-amber flex-1 sm:flex-none justify-center" disabled={!canRefund} onClick={() => setShowRefund(true)}>
                Refund
              </button>
            )}
            {p.status === "pending" && (
              <button className="btn btn-outline flex-1 sm:flex-none justify-center" disabled={!canVoid} onClick={() => setShowVoid(true)}>
                Void
              </button>
            )}
            {success && <span className="text-sm text-emerald-700 sm:ml-2 break-words">{success}</span>}
            {err && <span className="text-sm text-rose-700 sm:ml-2 break-words">{err}</span>}
          </div>
        </div>
        </div>
      </div>

      <PostingResultDialog
        open={resultOpen}
        variant={resultVariant}
        receiptNo={resultInfo.receiptNo}
        journalNo={resultInfo.journalNo}
        message={resultInfo.message}
        paymentId={id}
        onClose={() => {
          setResultOpen(false);
          // For modal usage, just close; navigation is managed by parent if needed
          // Keep compatibility with explicit redirect paths if parent passes them
          // No-op here
        }}
      />

      {/* Refund Modal */}
      {showRefund && (
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/40" onClick={() => !refundBusy && setShowRefund(false)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl w-[480px] max-w-[95vw] p-4">
            <div className="text-lg font-semibold mb-2">Refund Payment</div>
            <div className="text-sm text-ink/60 mb-2">Provide a reason. This posts a reversing journal.</div>
            <label className="block mb-3">
              <div className="text-xs text-ink/60">Reason</div>
              <input className="input w-full" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} maxLength={200} />
            </label>
            <div className="flex justify-end gap-2">
              <button className="btn btn-outline" disabled={refundBusy} onClick={() => setShowRefund(false)}>Cancel</button>
              <button className="btn btn-amber" disabled={!refundReason.trim() || refundBusy} onClick={async () => { setRefundBusy(true); try { const call = httpsCallable(functions, "refundPayment"); const { data } = await call({ paymentId: id, reason: refundReason.trim() }); const r = data || {}; setRefundInfo({ ok: !!r.ok, journalId: r.refundJournalId || "", already: !!r.already }); setShowRefund(false); } catch (e) { notify(e?.message || String(e), { type: "error" }); } finally { setRefundBusy(false); } }}>
                {refundBusy ? "Refunding…" : "Confirm Refund"}
              </button>
            </div>
          </div>
        </div>
      )}

    {/* Void Modal */}
      {showVoid && (
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/40" onClick={() => !voidBusy && setShowVoid(false)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl w-[480px] max-w-[95vw] p-4">
            <div className="text-lg font-semibold mb-2">Void Payment</div>
            <div className="text-sm text-ink/60 mb-2">Provide a reason. No journals will be created.</div>
            <label className="block mb-3">
              <div className="text-xs text-ink/60">Reason</div>
              <input className="input w-full" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} maxLength={200} />
            </label>
            <div className="flex justify-end gap-2">
              <button className="btn btn-outline" disabled={voidBusy} onClick={() => setShowVoid(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!voidReason.trim() || voidBusy} onClick={async () => { setBusy(true); try { const call = httpsCallable(functions, "voidPayment"); await call({ paymentId: id, reason: voidReason.trim() }); setShowVoid(false); } catch (e) { notify(e?.message || String(e), { type: "error" }); } finally { setBusy(false); } }}>
                {voidBusy ? "Voiding…" : "Confirm Void"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
